import { createSlice, PayloadAction } from '@reduxjs/toolkit';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface OrderRulesFilterCustomer {
  customerId: string;
  customerDisplayName: string;
  keyAccountName: string;
}

export interface OrderRulesFilterMaterial {
  materialId: string;
  materialName: string;
  packageId: string;
  packageName: string;
  alliedFlag: string;
}

interface OrderRulesFilterDataState {
  customers: OrderRulesFilterCustomer[];
  materials: OrderRulesFilterMaterial[];
  loading: boolean;
  error: string | null;
  /** Timestamp of last successful fetch — used to skip re-fetching on re-mount */
  fetchedAt: number | null;
}

const initialState: OrderRulesFilterDataState = {
  customers: [],
  materials: [],
  loading: false,
  error: null,
  fetchedAt: null,
};

// ─── Slice ────────────────────────────────────────────────────────────────────

const orderRulesFilterDataSlice = createSlice({
  name: 'orderRulesFilterData',
  initialState,
  reducers: {
    setOrderRulesFilterDataLoading(state) {
      state.loading = true;
      state.error = null;
    },

    setOrderRulesFilterData(
      state,
      action: PayloadAction<{
        customers: OrderRulesFilterCustomer[];
        materials: OrderRulesFilterMaterial[];
      }>,
    ) {
      state.customers = action.payload.customers;
      state.materials = action.payload.materials;
      state.loading = false;
      state.error = null;
      state.fetchedAt = Date.now();
    },

    setOrderRulesFilterDataError(state, action: PayloadAction<string>) {
      state.loading = false;
      state.error = action.payload;
    },

    /** Merge autocomplete customer results into the existing list (dedup by customerId). */
    appendOrderRulesFilterCustomers(
      state,
      action: PayloadAction<OrderRulesFilterCustomer[]>,
    ) {
      const existingIds = new Set(state.customers.map((c) => c.customerId));
      const newItems = action.payload.filter((c) => !existingIds.has(c.customerId));
      state.customers = [...state.customers, ...newItems];
    },

    /** Merge autocomplete material results into the existing list (dedup by materialId). */
    appendOrderRulesFilterMaterials(
      state,
      action: PayloadAction<OrderRulesFilterMaterial[]>,
    ) {
      const existingIds = new Set(state.materials.map((m) => m.materialId));
      const newItems = action.payload.filter((m) => !existingIds.has(m.materialId));
      state.materials = [...state.materials, ...newItems];
    },

    resetOrderRulesFilterData(state) {
      state.customers = [];
      state.materials = [];
      state.loading = false;
      state.error = null;
      state.fetchedAt = null;
    },
  },
});

export const {
  setOrderRulesFilterDataLoading,
  setOrderRulesFilterData,
  setOrderRulesFilterDataError,
  appendOrderRulesFilterCustomers,
  appendOrderRulesFilterMaterials,
  resetOrderRulesFilterData,
} = orderRulesFilterDataSlice.actions;

export default orderRulesFilterDataSlice.reducer;
